import { useContext, createContext, useState, useEffect } from 'react'

import { clientsCards } from '../storage'

import { storageItem, getUserData, mockCardUrl, saveLocalStorageUserData, setImpersonation } from '@utils'

const HomeContext = createContext({})

import { useSocket } from '@src/utility/hooks/useSocket'

import auth from '@src/services/auth'
import visualizationConfig from '@src/services/visualizationConfig'

import { AbilityContext } from '@src/utility/context/Can'

function HomeProvider({ children }) {
  const ability = useContext(AbilityContext)

  const [pendenteCount, setPendenteCount] = useState(0)
  const [naoRealizadaCount, setNaoRealizadaCount] = useState(0)
  const [realizadaCount, setRealizadaCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [clients, setClients] = useState([])
  const [user, setUser] = useState(getUserData())
  const [loadingUserInfo, setLoadingUserInfo] = useState(true)
  // Config global de anti-captura (default seguro: ligado).
  const [visualizationSettings, setVisualizationSettings] = useState({
    blur_ficha: true,
    blur_modal_ph3a: true
  })

  const updateUserInfo = async () => {
    setLoadingUserInfo(true)
    const { data, status } = await auth.user()

    if (status !== 200 || !data) {
      return
    }

    const responseAbility = saveLocalStorageUserData(data, data?.access_token)
    if (Array.isArray(responseAbility)) {
      ability.update(responseAbility)
    }

    // O backend é a fonte de verdade do estado de impersonação; ressincronizar aqui
    // corrige o aviso caso o storage tenha divergido (aba nova, storage limpo etc.).
    setImpersonation(data?.impersonacao)

    const formattedUserData = {
      ...data?.user,
      ability: responseAbility
    }

    setUser(formattedUserData)
    setLoadingUserInfo(false)

    return formattedUserData
  }

  useSocket({
    channel: `presence-abrirFicha.${user?.id}`,
    event: 'SendOpenRecord',
    callback: (data) => {
      window.location = mockCardUrl(data?.data?.id_cliente, data?.data?.id, true)
    }
  })

  useEffect(() => {
    const storageClients = storageItem('get', clientsCards)

    if (!!storageClients) {
      setClients(JSON.parse(storageClients))
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      const response = await visualizationConfig.get()
      if (response?.status === 200 && response?.data) {
        setVisualizationSettings({
          blur_ficha: !!response.data.blur_ficha,
          blur_modal_ph3a: !!response.data.blur_modal_ph3a
        })
      }
      // Em erro: mantém o default seguro (ambos true).
    })()
  }, [user?.id])

  const handleSelectClient = (client) => {
    let toStorage = []
    
    setClients(prev => {
      const index = prev.findIndex(item => item.id === client.id)
      let newClients = [...prev]
  
      if (index > -1) {
        newClients[index] = client
      } else {
        if (newClients.length >= 10) {
          newClients.pop() // Remove o último item
        }
        newClients = [client, ...newClients] // Adiciona o novo cliente no início
      }
  
      toStorage = newClients
      return newClients
    })
  
    if (toStorage.length > 0) {
      storageItem('set', clientsCards, JSON.stringify(toStorage))
    }
  }

  const handleCloseClient = (client) => {
    let toStorage = []
    setClients(prev => {
      const temp = prev.filter(item => item.id !== client.id)

      toStorage = temp

      return temp
    })

    if (toStorage.length > 0) {
      storageItem('set', clientsCards, JSON.stringify(toStorage))
    } else {
      storageItem('remove', clientsCards)
    }
  }

  return (
    <HomeContext.Provider value={{
      pendenteCount,
      setPendenteCount,
      naoRealizadaCount,
      setNaoRealizadaCount,
      realizadaCount,
      setRealizadaCount,
      totalCount,
      setTotalCount,
      handleSelectClient,
      handleCloseClient,
      clients,
      user,
      setUser,
      loadingUserInfo,
      setLoadingUserInfo,
      updateUserInfo,
      visualizationSettings,
      setVisualizationSettings
    }}>
      {children}
    </HomeContext.Provider>
  )
}

function useHome() {
  return useContext(HomeContext)
}

export { HomeProvider, useHome }
